import React, { useState, useEffect, useCallback } from "react";
import { 
  DndContext, 
  DragOverlay, 
  closestCorners, 
  PointerSensor, 
  useSensor, 
  useSensors 
} from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { render } from "@react-email/render";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../Firebase";
import { base_url } from "../../Global";
import { HiPlus } from "react-icons/hi2";

import useBuilderStore from "./store/useBuilderStore";
import Sidebar from "./sidebar/Sidebar";
import Canvas from "./layout/Canvas";
import EmailTemplate from "./EmailTemplate";
import RecipientSelector from "./RecipientSelector";
import BlockRenderer from "./blocks/BlockRenderer";

const BlockPreview = ({ id, layout }) => {
  let block = null;
  // Handle preview prefix
  const cleanId = id.startsWith('preview-') ? id.replace('preview-', '') : id;

  if (cleanId.startsWith('toolbox-')) {
    const type = cleanId.replace('toolbox-', '');
    // Mock block for toolbox items
    block = { type, text: type.charAt(0).toUpperCase() + type.slice(1), fontSize: 16, color: '#000000', align: 'center', socials: [], src: '' };
  } else {
    for (const row of layout) {
      for (const col of row.columns) {
        const found = col.blocks.find(b => b.id === cleanId);
        if (found) {
          block = found;
          break;
        }
      }
    }
  }

  if (!block) return null;

  return (
    <div className="w-[300px] bg-white border-2 border-[#8B3DFF] rounded-lg shadow-2xl opacity-90 scale-105 rotate-2 pointer-events-none overflow-hidden">
       <div className="p-4">
          <BlockRenderer block={block} isPreview={true} />
       </div>
       <div className="bg-[#8B3DFF] px-3 py-1 text-[8px] font-black text-white uppercase tracking-widest text-center">
          {cleanId.startsWith('toolbox-') ? 'Placing New' : 'Moving'} {block.type}
       </div>
    </div>
  );
};

const CampaignBuilder = ({ appName }) => {
  const { 
    layout, 
    setLayout, 
    globalSettings, 
    addBlock, 
    addRow,
    moveRow,
    undo,
    redo,
    lastAction
  } = useBuilderStore();

  const [notificationTitle, setNotificationTitle] = useState("");
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [liveHtmlPreview, setLiveHtmlPreview] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [debouncedLayout, setDebouncedLayout] = useState(layout);
  const [debouncedGlobalSettings, setDebouncedGlobalSettings] = useState(globalSettings);

  // Debounce layout updates for preview compilation
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedLayout(layout);
      setDebouncedGlobalSettings(globalSettings);
    }, 500);
    return () => clearTimeout(timer);
  }, [layout, globalSettings]);

  // Keyboard Shortcuts (Undo/Redo)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd/Ctrl + Z
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        if (e.shiftKey) {
          e.preventDefault();
          redo();
        } else {
          e.preventDefault();
          undo();
        }
      }
      // Check for Cmd/Ctrl + Y (Alternative Redo)
      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // Descriptive Notifications for Undo/Redo
  useEffect(() => {
    if (lastAction) {
      const { type, name } = lastAction;
      const icon = type === 'undo' ? '↩️' : '↪️';
      const label = type === 'undo' ? 'Reverted' : 'Redone';
      
      toast.info(`${icon} ${label}: ${name}`, {
        position: "bottom-left",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "dark",
      });
    }
  }, [lastAction]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  useEffect(() => {
    const compileEmail = async () => {
      try {
        const htmlString = await render(
          <EmailTemplate layout={debouncedLayout} globalSettings={debouncedGlobalSettings} />,
          { pretty: true }
        );
        setLiveHtmlPreview(htmlString);
      } catch (error) {
        console.error("Email compilation error:", error);
      }
    };
    compileEmail();
  }, [debouncedLayout, debouncedGlobalSettings]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    const activeId = activeData?.originalId || active.id;
    const overId = overData?.originalId || over.id;

    // Handle cross-column movement during drag for smooth UI
    if (activeData?.type === 'block') {
      const activeColId = activeData.columnId;
      const overColId = overData?.type === 'block' ? overData.columnId : overData?.type === 'column' ? (overData.originalId || over.id) : null;

      if (overColId && activeColId !== overColId) {
        const newLayout = JSON.parse(JSON.stringify(layout)); // Deep copy to avoid mutation
        
        let sourceCol, destCol;
        for (const row of newLayout) {
          for (const col of row.columns) {
            if (col.id === activeColId) sourceCol = col;
            if (col.id === overColId) destCol = col;
          }
        }

        if (sourceCol && destCol) {
          const activeIndex = sourceCol.blocks.findIndex(b => b.id === activeId);
          if (activeIndex !== -1) {
            const [movedBlock] = sourceCol.blocks.splice(activeIndex, 1);
            movedBlock.columnId = overColId;
            
            const overIndex = destCol.blocks.findIndex(b => b.id === overId);
            const insertIndex = overIndex >= 0 ? overIndex : destCol.blocks.length;
            
            destCol.blocks.splice(insertIndex, 0, movedBlock);
            
            // Update the block's columnId in the data as well
            active.data.current.columnId = overColId;
            setLayout(newLayout);
          }
        }
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    const activeId = activeData?.originalId || active.id;
    const overId = overData?.originalId || over.id;

    // 1. Drop from Toolbox
    if (activeData?.type === 'toolbox-item') {
      const toolType = activeData.toolType;
      
      if (toolType === 'row') {
        addRow();
      } else {
        const targetColumnId = overData?.type === 'column' 
          ? (overData.originalId || over.id) 
          : overData?.type === 'block' 
            ? overData.columnId 
            : null;
        
        if (targetColumnId) {
          // Drop onto specific column or block
          const overIndex = overData?.type === 'block' 
            ? overData.index 
            : undefined;
          addBlock(targetColumnId, toolType, overIndex);
        } else if (layout.length > 0) {
          addBlock(layout[0].columns[0].id, toolType);
        } else {
          toast.info("Add a row first before adding blocks.");
        }
      }
      return;
    }

    // 2. Reorder Rows
    if (activeData?.type === 'row' && overData?.type === 'row') {
      if (activeId !== overId) {
        const oldIndex = layout.findIndex((r) => r.id === activeId);
        const newIndex = layout.findIndex((r) => r.id === overId);
        moveRow(oldIndex, newIndex);
      }
      return;
    }

    // 3. Finalize Reorder within column (Cross-column handled in handleDragOver)
    if (activeData?.type === 'block' && overData?.type === 'block') {
      if (activeId !== overId && activeData.columnId === overData.columnId) {
        const newLayout = JSON.parse(JSON.stringify(layout));
        let targetCol;
        for (const row of newLayout) {
          for (const col of row.columns) {
            if (col.id === activeData.columnId) targetCol = col;
          }
        }

        if (targetCol) {
          const oldIndex = targetCol.blocks.findIndex(b => b.id === activeId);
          const newIndex = targetCol.blocks.findIndex(b => b.id === overId);
          targetCol.blocks = arrayMove(targetCol.blocks, oldIndex, newIndex);
          setLayout(newLayout);
        }
      }
    }
  };

  const sendEmail = async (event) => {
    event.preventDefault();
    if (!notificationTitle.trim()) {
      toast.error("Subject Title cannot be empty");
      return;
    }
    if (!selectedRecipients.length) {
      toast.error("Please select at least one recipient");
      return;
    }
    if (!liveHtmlPreview) {
      toast.error("Email content is not ready.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        subject: notificationTitle,
        to: selectedRecipients,
        app: appName,
        htmlBody: liveHtmlPreview
      };

      const res = await fetch(base_url + "/auth/sendDirectEmail", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Campaign dispatched successfully!");
        setNotificationTitle("");
      } else {
        const error = await res.json();
        toast.error(`Failed to send: ${error.message || "Unknown error"}`);
      }
    } catch (error) {
      console.error(error);
      toast.error("Network error while dispatching campaign.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-[#0e1318] overflow-hidden font-sans text-slate-100">
      <ToastContainer position="bottom-right" theme="dark" />
      
      {/* Application Header (Dark) */}
      <header className="h-[72px] bg-[#0e1318] border-b border-white/5 flex items-center justify-between px-10 shrink-0 z-50 shadow-2xl">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gradient-to-tr from-[#8B3DFF] to-[#00C4CC] rounded-[1rem] flex items-center justify-center shadow-[0_8px_20px_rgba(139,61,255,0.3)] ring-1 ring-white/10">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
          </div>
          <div className="flex flex-col">
            <h1 className="text-sm font-black tracking-[0.1em] uppercase text-white leading-none">Campaign <span className="text-[#8B3DFF]">Builder</span></h1>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="px-4 py-1.5 bg-white/5 border border-white/5 rounded-full text-[10px] font-black text-slate-500 tracking-widest uppercase">
            Platform: <span className="text-[#00C4CC] ml-1">{appName}</span>
          </div>
          <div className="h-8 w-px bg-white/5 mx-2"></div>
          <button
            onClick={sendEmail}
            disabled={isLoading}
            className={`flex items-center gap-2 px-10 py-3 rounded-2xl font-black text-[11px] uppercase tracking-[0.15em] transition-all duration-300 shadow-xl ${isLoading ? 'bg-white/5 text-slate-600 cursor-not-allowed shadow-none' : 'bg-gradient-to-r from-[#8B3DFF] to-[#7025e6] text-white hover:shadow-[0_12px_32px_rgba(139,61,255,0.4)] hover:-translate-y-0.5 active:translate-y-0 active:scale-95'}`}
          >
            {isLoading ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> : null}
            {isLoading ? 'Dispatching...' : 'Send Campaign'}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex flex-1 overflow-hidden relative">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <Sidebar />
          
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Context bar (Dark) */}
            <div className="px-8 py-5 bg-[#0e1318] border-b border-white/5 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start shrink-0">
              <div className="lg:col-span-5 space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">Email Subject</label>
                <input
                  type="text"
                  placeholder={`Subject for ${appName.toUpperCase()}...`}
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  className="w-full px-5 py-3 rounded-2xl border border-white/5 bg-black/20 text-white text-sm font-medium focus:ring-4 focus:ring-[#8B3DFF]/10 focus:border-[#8B3DFF] outline-none transition-all placeholder:text-slate-800 shadow-inner"
                />
              </div>
              <div className="lg:col-span-7">
                <RecipientSelector 
                  appName={appName} 
                  onSelectionChange={setSelectedRecipients} 
                />
              </div>
            </div>

            {/* Editor Area (Dark Workspace) */}
            <div className="flex-1 overflow-hidden flex bg-[#0e1318]">
              <div className="flex-1 overflow-y-auto relative p-10 bg-[radial-gradient(#ffffff05_1px,transparent_1px)] [background-size:24px_24px]">
                 <div className="max-w-5xl mx-auto">
                    <Canvas />
                 </div>
              </div>
              
              {/* Refined Live Preview (Dark Frame) */}
              <div className="w-[480px] border-l border-white/5 bg-[#0e1318] p-10 overflow-hidden flex flex-col shrink-0 hidden 2xl:flex items-center">
                <div className="w-full flex items-center justify-between mb-8">
                  <div className="flex flex-col">
                    <h3 className="text-[10px] font-black text-white uppercase tracking-widest">Mobile Sync</h3>
                    <span className="text-[9px] text-slate-600 font-bold uppercase tracking-tight">Interactive Preview</span>
                  </div>
                  <div className="px-3.5 py-1.5 bg-[#00C4CC]/10 rounded-full border border-[#00C4CC]/20 flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#00C4CC] animate-pulse shadow-[0_0_8px_#00C4CC]"></div>
                    <span className="text-[9px] text-[#00C4CC] font-black uppercase">Live</span>
                  </div>
                </div>
                
                {/* High-Fidelity Mobile Frame */}
                <div className="w-full max-w-[300px] aspect-[9/19.5] bg-black rounded-[3.5rem] border-[12px] border-[#1a1a1e] shadow-[0_40px_100px_-12px_rgba(0,0,0,0.5)] overflow-hidden relative ring-1 ring-white/10 ring-inset">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-7 bg-[#1a1a1e] rounded-b-3xl z-20 flex items-center justify-center shadow-2xl">
                    <div className="w-8 h-1 bg-[#2a2a2e] rounded-full" />
                  </div>
                  <div className="w-full h-full absolute inset-0 bg-white overflow-y-auto">
                    <div style={{ transform: `scale(${300 / globalSettings.containerWidth})`, transformOrigin: 'top left', width: `${globalSettings.containerWidth}px` }}>
                      <Canvas isPreview={true} idPrefix="preview-" scale={300 / globalSettings.containerWidth} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DragOverlay dropAnimation={{ duration: 250, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
            {activeId ? <BlockPreview id={activeId} layout={layout} /> : null}
          </DragOverlay>
        </DndContext>
      </main>
    </div>
  );
};

export default CampaignBuilder;
